import crypto from 'crypto';
import { logger } from './logger';

interface CacheEntry {
	query: string;  // The query string (for debugging/inspection)
	data: any;
	timestamp: number;
}

/**
 * Query Cache — Two mechanisms:
 *
 * 1. `cache` (query string → result data) — TTL-based with max size, for avoiding re-execution
 *    of recently validated queries. True LRU eviction: reads bubble entries to the back via
 *    delete+re-set so the oldest *unused* entry is evicted, not the oldest *inserted*.
 *
 * 2. Encrypted queryId tokens — SQL is encrypted into the queryId itself (self-contained).
 *    No server-side storage needed for SQL mappings. The token is decrypted on each request.
 *    This eliminates the unbounded queryIdCache that previously grew forever and caused
 *    memory bloat (hundreds of MBs after thousands of queries).
 *
 *    Result data can still be cached temporarily via the data cache (mechanism 1).
 */
class QueryCache {
	private cache: Map<string, CacheEntry> = new Map();
	private ttlMs: number = 10 * 60 * 1000; // Default: 10 minutes
	private maxCacheSize: number = 5000;      // Max data cache entries
	private cleanupInterval: NodeJS.Timeout | null = null;

	// Encryption for queryId tokens
	private readonly algorithm = 'aes-256-gcm';
	private encryptionKey: Buffer;

	constructor() {
		// Derive a stable encryption key from an env variable or a default.
		// The key must be consistent across the process lifetime so tokens
		// generated before a hot-reload still decrypt correctly.
		// On PM2 restart, old tokens in cached conversations are re-registered
		// via _queryMap rehydration, so key rotation on restart is acceptable.
		const keySource = process.env.QUERY_TOKEN_SECRET || 'superatom-query-cache-default-key-v1';
		this.encryptionKey = crypto.createHash('sha256').update(keySource).digest();

		this.startCleanup();
	}

	// ============================================
	// Data Cache (TTL-based, max size)
	// ============================================

	/**
	 * Set the cache TTL (Time To Live)
	 * @param minutes - TTL in minutes (default: 10)
	 */
	setTTL(minutes: number): void {
		this.ttlMs = minutes * 60 * 1000;
		logger.info(`[QueryCache] TTL set to ${minutes} minutes`);
	}

	/**
	 * Get the current TTL in minutes
	 */
	getTTL(): number {
		return this.ttlMs / 60 / 1000;
	}

	/**
	 * Store query result in data cache.
	 * If the key already exists, it's removed first so the re-insert places it
	 * at the back of the iteration order (LRU). Eviction only fires when adding
	 * a genuinely new key past the size limit.
	 */
	set(query: string, data: any): void {
		// Drop the existing entry (if any) so re-insert moves it to the back.
		// Also keeps the size check accurate for genuinely-new keys.
		this.cache.delete(query);

		// Evict oldest only if we're about to grow past the cap.
		if (this.cache.size >= this.maxCacheSize) {
			const oldestKey = this.cache.keys().next().value;
			if (oldestKey) this.cache.delete(oldestKey);
		}

		this.cache.set(query, {
			query,
			data,
			timestamp: Date.now()
		});
		logger.debug(`[QueryCache] Stored result for query (${query.substring(0, 50)}...)`);
	}

	/**
	 * Get cached result if exists and not expired.
	 * On hit, re-inserts the entry so it moves to the back of the Map's
	 * iteration order — turning FIFO eviction into true LRU.
	 */
	get(query: string): any | null {
		const entry = this.cache.get(query);
		if (!entry) return null;

		if (Date.now() - entry.timestamp > this.ttlMs) {
			this.cache.delete(query);
			return null;
		}

		// LRU bump: re-insert so this key becomes the newest in iteration order.
		this.cache.delete(query);
		this.cache.set(query, entry);

		logger.info(`[QueryCache] Cache HIT for query (${query.substring(0, 50)}...)`);
		return entry.data;
	}

	/**
	 * Check if query exists in cache (not expired)
	 */
	has(query: string): boolean {
		return this.get(query) !== null;
	}

	/**
	 * Remove a specific query from cache
	 */
	delete(query: string): void {
		this.cache.delete(query);
	}

	/**
	 * Clear all cached entries
	 */
	clear(): void {
		this.cache.clear();
		logger.info('[QueryCache] Cache cleared');
	}

	/**
	 * Get cache statistics
	 */
	getStats(): { size: number; queryIdCount: number; oldestEntryAge: number | null } {
		let oldestTimestamp: number | null = null;
		for (const entry of this.cache.values()) {
			if (oldestTimestamp === null || entry.timestamp < oldestTimestamp) {
				oldestTimestamp = entry.timestamp;
			}
		}
		return {
			size: this.cache.size,
			queryIdCount: 0, // No longer stored in memory
			oldestEntryAge: oldestTimestamp ? Date.now() - oldestTimestamp : null
		};
	}

	/**
	 * Start periodic cleanup of expired data cache entries.
	 */
	private startCleanup(): void {
		this.cleanupInterval = setInterval(() => {
			const now = Date.now();
			let expiredCount = 0;
			for (const [key, entry] of this.cache.entries()) {
				if (now - entry.timestamp > this.ttlMs) {
					this.cache.delete(key);
					expiredCount++;
				}
			}
			if (expiredCount > 0) {
				logger.debug(`[QueryCache] Cleaned up ${expiredCount} expired data-cache entries (${this.cache.size} remaining)`);
			}
		}, 2 * 60 * 1000);
	}

	// ============================================
	// Encrypted Query ID Tokens (zero memory)
	// ============================================

	/**
	 * Encrypt a payload into a self-contained token.
	 */
	private encrypt(payload: string): string {
		const iv = crypto.randomBytes(12);
		const cipher = crypto.createCipheriv(this.algorithm, this.encryptionKey, iv);
		let encrypted = cipher.update(payload, 'utf8', 'base64');
		encrypted += cipher.final('base64');
		const authTag = cipher.getAuthTag();
		// Pack: iv(12) + authTag(16) + ciphertext — all base64
		return iv.toString('base64') + '.' + authTag.toString('base64') + '.' + encrypted;
	}

	/**
	 * Decrypt a token back to the original payload.
	 */
	private decrypt(token: string): string | null {
		try {
			const parts = token.split('.');
			if (parts.length !== 3) return null;

			const iv = Buffer.from(parts[0], 'base64');
			const authTag = Buffer.from(parts[1], 'base64');
			const encrypted = parts[2];

			const decipher = crypto.createDecipheriv(this.algorithm, this.encryptionKey, iv);
			decipher.setAuthTag(authTag);
			let decrypted = decipher.update(encrypted, 'base64', 'utf8');
			decrypted += decipher.final('utf8');
			return decrypted;
		} catch (err) {
			logger.warn(`[QueryCache] Failed to decrypt queryId token: ${err instanceof Error ? err.message : String(err)}`);
			return null;
		}
	}

	/**
	 * Store a query by generating an encrypted token as queryId.
	 * The SQL is encrypted INTO the token — nothing stored in memory.
	 * If data is provided, it's cached temporarily in the data cache.
	 */
	storeQuery(query: any, data?: any): string {
		const payload = typeof query === 'string' ? query : JSON.stringify(query);
		const queryId = this.encrypt(payload);

		// Cache the result data temporarily (if provided) using the token as key
		if (data) {
			this.set(queryId, data);
		}

		const queryPreview = payload.substring(0, 50);
		logger.debug(`[QueryCache] Stored query as encrypted token (${queryPreview}...)`);
		return queryId;
	}

	/**
	 * Get a stored query by decrypting its token.
	 * Returns the SQL + any cached result data.
	 */
	getQuery(queryId: string): { query: any; data: any } | null {
		const decrypted = this.decrypt(queryId);
		if (!decrypted) return null;

		// Try to parse as JSON (for object queries), fall back to plain string
		let query: any;
		try {
			query = JSON.parse(decrypted);
		} catch {
			query = decrypted;
		}

		// Check data cache for cached result
		const cachedData = this.get(queryId);

		return { query, data: cachedData };
	}

	/**
	 * Update cached data for a queryId token
	 */
	setQueryData(queryId: string, data: any): void {
		this.set(queryId, data);
	}

	/**
	 * Stop cleanup interval (for graceful shutdown)
	 */
	destroy(): void {
		if (this.cleanupInterval) {
			clearInterval(this.cleanupInterval);
			this.cleanupInterval = null;
		}
		this.cache.clear();
	}
}

// Export singleton instance
export const queryCache = new QueryCache();
